<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import DialogRevamp from '@/components/DialogRevamp.vue'
import InputFieldFloatingWithMessage from '@/components/ui/input/InputFieldFloatingWithMessage.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import useAuthDialog from '@/lib/useAuthDialog'
import injectStrict from '@/lib/inject'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	emailLogin: {
		type: String,
		default: '',
	},
})
const emit = defineEmits(['hide', 'backStep', 'submit', 'updateEmail'])
const { isShowDialog } = useAuthDialog(hideDialogDirectly)
const textContent: any = injectStrict('textContent')

const isLoading = ref(false)
const formState = reactive({
	email: props.emailLogin,
})
const rules = {
	email: {
		required,
		email,
	},
}
const $v = useVuelidate(rules, formState)
const emailErrorMessage = computed(() => {
	if ($v.value.email.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.email.email.$invalid) {
		return textContent.input_error_email_invalid
	}
	return ''
})

function backStep() {
	emit('backStep')
}

function submit() {
	$v.value.$touch()
	if ($v.value.$invalid) {
		return
	}
	emit('submit', {
		email: formState.email,
	})
}

function hideDialogDirectly() {
	emit('hide')
}

watch(
	() => formState.email,
	value => {
		emit('updateEmail', value)
	},
)
</script>

<template>
	<DialogRevamp
		class="bs-auth-forget"
		v-model="isShowDialog"
		:isFullInMobile="true"
		:isCustomOverlayFunction="true"
		:isShowDefaultClose="false"
		:size="'sm'"
		:isSecondLayer="true"
		@overlayClick="backStep"
	>
		<template #header>
			<div class="bs-auth-back-header">
				<button class="bs-icon bs-auth-back-header__back" @click="backStep">
					<BaseIcon :name="'back'" class="h-16px w-16px fill-n-950" />
				</button>
				<h5 class="bs-auth__title">
					{{ textContent.reset_password_title }}
				</h5>
			</div>
		</template>
		<template #content>
			<div class="bs-auth-forget__content">
				<p class="bs-auth-forget__content__description">
					{{ textContent.reset_password_description }}
				</p>
				<InputFieldFloatingWithMessage
					class="bs-auth-forget__content__email"
					v-model="$v.email.$model"
					type="email"
					:label="textContent.input_label_email"
					:isError="$v.email.$error"
					:errorMessage="emailErrorMessage"
				></InputFieldFloatingWithMessage>
			</div>
		</template>
		<template #footer>
			<BaseButton
				:size="'large'"
				:isFull="true"
				:isLoading="isLoading"
				@click="submit"
			>
				{{ textContent.button_next_step }}
			</BaseButton>
		</template>
	</DialogRevamp>
</template>
